import { getAnalytics } from "firebase/analytics";
import { firebaseConfig } from "./config"
import firebase from 'firebase/compat/app'
import 'firebase/compat/auth'
import 'firebase/compat/firestore'
import { v4 as uuidv4 } from 'uuid';
import { Timestamp } from 'firebase/firestore';
import { isSignInWithEmailLink, sendSignInLinkToEmail, signInWithEmailLink } from "firebase/auth";

import { isDropdownSelection, isMultipleChoice, isMultipleChoiceOther, isMultipleSelection, isRating, isScale, isShortResponse, isYesNo, Question, QuestionResponseResult, Response, Result, Survey, User } from '../types';

// Initialize Firebase
const app = firebase.initializeApp(firebaseConfig);
const analytics = getAnalytics(app);

export const auth = firebase.auth();
export const firestore = firebase.firestore();
// export const storage = firebase.storage();

const GoogleAuthProvider: firebase.auth.GoogleAuthProvider = new firebase.auth.GoogleAuthProvider();
GoogleAuthProvider.setCustomParameters({ prompt: 'select_account' });

export const signInWithGoogle = async  (): Promise<User> => {
    const user = await auth.signInWithPopup(GoogleAuthProvider);
    const userIdToken = await user.user?.getIdToken();
    if(user?.user?.displayName && user?.user?.email && user?.user?.uid){
        createUser({uid: user?.user?.uid, displayName: user?.user?.displayName, email: user?.user?.email} as User);
        return {uid: user?.user?.uid, displayName: user?.user?.displayName, email: user?.user?.email, userIdToken: userIdToken} as User;
    }else{
        return {} as User;
    }
};

export const emailPasswordSignUp = async (email: string, password: string): Promise<User> => {
 try {
  const user = await auth.createUserWithEmailAndPassword(email, password);
  const userIdToken = await user.user?.getIdToken();
  if(user?.user?.email && user?.user?.uid){
      createUser({uid: user?.user?.uid, displayName: '', email: user?.user?.email} as User);
      return {uid: user?.user?.uid, displayName: '', email: user?.user?.email, userIdToken: userIdToken} as User;
  }else{
      return {} as User;
  }
 }catch(error: any){
    console.error(error);
    throw new Error('Error creating user');
 }
};

export const emailPasswordSignIn = async (email: string, password: string): Promise<User> => {
    const user = await auth.signInWithEmailAndPassword(email, password);
    const userIdToken = await user.user?.getIdToken();
    if(user?.user?.email && user?.user?.uid){
        return {uid: user?.user?.uid, displayName: '', email: user?.user?.email, userIdToken: userIdToken} as User;
    }else{
        return {} as User;
    }
};

export const manageAuth = firebase.auth().onAuthStateChanged(async (user) => {
    if(user
        && user.email
        && user.displayName
        && user.uid){
    }
});
    
export const signOut = () => auth.signOut();

export const createUser = async (user: User) => {
    try{
        const userRef = firestore.collection('users');
        const snapshot = await userRef.where('uid', '==', user.uid).get();
        if(snapshot.empty){
            await userRef.doc(user.email).set(user);
        }
    }catch(error){
        console.log(error);
    }
}

export const generateSurvey = async (user: User, survey: Survey): Promise<Survey | undefined> => {
  console.log('Generating survey...', survey);
    try {
        const surveyId = survey.id.trim() === '' ? uuidv4() : survey.id;
        const surveyResponses = survey.responses || [];
        const surveyRef = firestore.collection('surveys').doc(surveyId);
        await surveyRef.set({...survey, responses:surveyResponses, creator:user.email, id: surveyId, createdAt: Timestamp.fromDate(new Date()), updatedAt: Timestamp.fromDate(new Date())});

        return {...survey, creator:user.email, id: surveyId, createdAt: Timestamp.fromDate(new Date()), updatedAt: Timestamp.fromDate(new Date())} as Survey;

    } catch (error) {

        console.log(error);
        return undefined; // Ensure a return value in case of error

    }
};

export const updateSurveyDescription = async (survey: Survey, description: string) => {
  const newSurvey = {
    ...survey,
    description,
    updatedAt: new Date(),
  }
    try{
        const surveyRef = firestore.collection('surveys').doc(survey.id);
        await surveyRef.update({description});
        return newSurvey;
    }catch(error){
        console.log(error);
    }
}

export const fetchSurveys = async (user: User) => {
    try{
        const surveyRef = firestore.collection('surveys').where('creator', '==', user.email);
        const surveys = await surveyRef.get();

        if(surveys.empty){
            console.log('No surveys found');
            return [];
        }
        const surveysList = surveys.docs.map((doc: any) => {
            return { ...doc.data(), id: doc.data().id } as Survey;
        });
        return surveysList;
    }catch(error){
        console.log(error);
    }
};

export const fetchSurvey = async (user: User, surveyId: string): Promise<Survey | undefined> => {
    try {
        const surveyRef = firestore.collection('surveys').where('id', '==', surveyId);
        const surveyDoc = await surveyRef.get();
        if (surveyDoc.docs.length) {
            return { ...surveyDoc.docs[0].data(), id: surveyDoc.docs[0].data().id } as Survey;
        } else {
            console.log('No such document!');
            return undefined;
        }
    } catch (error) {
        console.log(error);
        return undefined;
    }
};

export const deleteSurvey = async (user: User, survey: Survey) => {
    try{
        const surveyRef = firestore.collection('surveys').doc(survey.id);
        await surveyRef.delete();
    }catch(error){
        console.log(error);
    }
};

const processQuestion = (question: Question) => {
    if (isMultipleChoice(question.questionBody)) {
        return question.questionBody.options.map((option) => ({ option, count: 0 }));
    } else if (isMultipleSelection(question.questionBody)) {
        return question.questionBody.options.map((option) => ({ option, count: 0 }));    
    } else if (isDropdownSelection(question.questionBody)) {
        return question.questionBody.options.map((option) => ({ option, count: 0 }));
    } else if (isShortResponse(question.questionBody)) {
        return[];
    // } else if (isMultipleChoiceOther(question.questionBody)) {
    //     console.log('MultipleChoiceOther options:', question.questionBody.options);
    //     console.log('Other question text:', question.questionBody.other.questionText);
    } else if (isScale(question.questionBody)) {
        return [];
    } else if (isRating(question.questionBody)) {
        return[];
    } else if (isYesNo(question.questionBody)) {
        return [{ option:'yes', count: 0 }, {option:'no', count: 0}];
    } else {
        console.log('Unknown question type');
    }
};

export const addSurveyQuestion = async (user: User, survey: Survey, question: Question) => {
    console.log('Adding question...');
    console.log(user);
    console.log(survey);
    console.log(question);
    const results = processQuestion(question);
    console.log('sjdaskcksdjcjkadscbjksdkhfbskfbsdukj', results);
    try{
        const surveyRef = firestore.collection('surveys').doc(survey.id);
        await surveyRef.update({
            ...survey,
            updatedAt: new Date(),
            questions: firebase.firestore.FieldValue.arrayUnion(question),
            responses: firebase.firestore.FieldValue.arrayUnion(
                {questionId: question.id,
                questionText: question.questionText,
                questionType: question.type,
                results:results})
            
        });
    }catch(error){
        console.log(error);
    }
};

export const deleteSurveyQuestion = async (survey: Survey, question: Question) => {
  console.log('Deleting question...', question);
  console.log('Survey...', survey);
    try{
        const surveyRef = firestore.collection('surveys').doc(survey.id);
        await surveyRef.update({
            ...survey,
            updatedAt: new Date(),
            questions: firebase.firestore.FieldValue.arrayRemove(question),
            responses: firebase.firestore.FieldValue.arrayRemove(survey?.responses?.find(response => response.questionId === question.id))
        });
    }catch(error){
        console.log(error);
        throw new Error('Error deleting question');
    }
};

export const updateSurveyQuestion = async (user: User, survey: Survey, updatedQuestion: Question) => {
    try {
        console.log('Updating question...');
        const surveyRef = firestore.collection('surveys');
        console.log('SurveyRef:', surveyRef);
        const surveyDoc = await surveyRef.where('id', '==', survey.id).get();
        console.log('SurveyDoc:', surveyDoc);
        if (!surveyDoc.empty) {
            const surveyData = surveyDoc.docs[0].data();
            if (surveyData && surveyData.questions) {
                const updatedQuestions = surveyData.questions.map((question: Question) =>
                    question.id === updatedQuestion.id ? updatedQuestion : question
                );
                const currentResponseResults = surveyData.responses.find((response: QuestionResponseResult) => response.questionId === updatedQuestion.id);
                console.log('Current Response Results:', currentResponseResults);
                let updatedResults: any = [];
                if(isMultipleChoice(updatedQuestion.questionBody) || isDropdownSelection(updatedQuestion.questionBody) || isMultipleSelection(updatedQuestion.questionBody)) {
                    console.log('Updated Question:', updatedQuestion.questionBody.options);
                    updatedResults = updatedQuestion.questionBody.options.map((option: string) => ({ 
                        option, 
                        count: currentResponseResults?.results.find((result: Result) => result.option === option)?.count || 0
                     }));
                }
                const updatedResponseQuestionText = surveyData.responses.map((response: QuestionResponseResult) =>
                    response.questionId === updatedQuestion.id ? {...response, questionText: updatedQuestion.questionText, results: updatedResults} : response
                );
                    console.log('Updated Response Results:', {...updatedResults});
                console.log('Updated Questions:', survey);
                // console.log('FINAL FINAL FINAL', {...survey, updatedAt: new Date(), createdAt: new Date(), questions: updatedQuestions, responses: updatedResponseQuestionText});
                await surveyRef.doc(surveyDoc.docs[0].id).update({...survey, updatedAt: new Date(), createdAt: new Date(), questions: updatedQuestions, responses: updatedResponseQuestionText});
            }
        }
    } catch (error) {
        console.log(error);
    }
};

export const checkSurveyResponseEligibility = async (user: User, survey: Survey) => {
    try{
        const surveyRef = firestore.collection('surveys').doc(survey.id).collection('responses');
        const response = await surveyRef.doc(user.email).get();
        console.log(response);
        return response.exists;
    }catch(error){
        console.log(error);
    }
}

export const updateSurveyResponses = async (user: User, survey: Survey, response: Response) => {
    try{
        let tempResults: QuestionResponseResult[] = [];
        const surveyData = (await firestore.collection('surveys').doc(survey.id).get()).data();
        let updatedResponses: QuestionResponseResult[] = surveyData?.responses;

        if(updatedResponses){
            response.responses.map(response => {
                let temp: Result[] = [];
                const result = updatedResponses.find((result: QuestionResponseResult) => result.questionId === response.questionId);
                // console.log('Result:', result);
                switch(result?.questionType){
                    case 'Multiple Choice':
                    case 'Dropdown Selection':
                    case 'YesNo':
                      console.log('Multiple Choice or Dropdown Selection or YesNo')
                        console.log(result.questionText);
                        result.results.map((result: any) => {
                            if(result.option === response.response){
                                temp.push({option: result.option, count: result.count + 1});
                            }else{
                                temp.push(result) ;
                            }
                        });
                        console.log(result.questionType, result);
                        tempResults.push({...result, results: temp});
                        break;
                    case 'Multiple Selection':
                        result.results.map((selectedOption: any) => {
                            if (Array.isArray(response.response) && response.response.includes(selectedOption.option)) {
                                temp.push({ option: selectedOption.option, count: selectedOption.count + 1 });
                            } else {
                                temp.push(selectedOption);
                            }
                        });
                        tempResults.push({...result, results: temp});
                        console.log(temp);
                        console.log(result.questionType, result);
                        break;
                    case 'Short Response':
                    case 'Scale':
                    case 'Rating':
                        tempResults.push({...result, results:[...result.results, response.response]} as QuestionResponseResult);
                        break;
                    default:
                      console.log('Unknown question type', result?.questionText);
                        break;

                }
                // if(result){
                //     const updatedResults = result.results.map((result: any) => {
                //         if(result.option === response.response){
                //             return {option: result.option, count: result.count + 1};
                //         }else{
                //             return result;
                //         }
                //     });
                //     updatedResponses = updatedResponses.map((result: QuestionResponseResult) => {
                //         if(result.questionId === response.questionId){
                //             return {...result, results: updatedResults};
                //         }else{
                //             return result;
                //         }
                //     });
                // }
            });
        }
        console.log('Temp Results:', tempResults);
        await firestore.collection('surveys').doc(survey.id).update({responses: tempResults});

        // response.responses.map(response => {
        //     const question = surveyData?.questions.find((question: Question) => question.id === response.questionId);
        //     if (question) {
        //         const questionResults = surveyData?.responses.find((result: QuestionResponseResult) => result.questionId === response.questionId);
        //         if (questionResults) {
        //             const updatedResults = questionResults.results.map((result: any) => {
        //                 if (result.option === response.response) {
        //                     return { option: result.option, count: result.count + 1 };
        //                 } else {
        //                     return result;
        //                 }
        //             });
        //             surveyData?.responses.map((result: QuestionResponseResult) => {
        //                 if (result.questionId === response.questionId) {
        //                     return { ...result, results: updatedResults };
        //                 } else {
        //                     return result;
        //                 }
        //             });
        //         }
        //     }
        // })
    }catch(error){
        console.log(error);
    }
}

export const generateSurveyResponse = async (user: User, survey: Survey, response: Response) => {
    console.log('Generating Survey Response...');
    console.log(user);
    console.log(survey);
    console.log(response);
    const completedAt = new Date();
    let duration = (completedAt.getTime() - response.createdAt.getTime()) / 1000 / 60;

    try{
        const surveyRef = firestore.collection('surveys').doc(survey.id).collection('responses');
        const newResponse = await surveyRef.doc(user.email).set({...response, survey, respondent: user.email, completedAt, duration});
        // const newResponse = await surveyRef.add({response: {...response, respondent: user.email, createdAt: new Date(), completedAt: null}});
        return newResponse;
    }catch(error){
        console.log(error);
    }
};

export const fetchSurveyResponses = async (surveyID: string, user?: User) => {
    try{
        const surveyRef = firestore.collection('surveys').doc(surveyID).collection('responses');
        const responses = await surveyRef.get();
        return responses;
    }catch(error){
        console.log(error);
    }
};

export const writeRandomNumbersToResults = async (surveyID: string, question: Question) => {
    const surveyRef = firestore.collection('surveys').doc(surveyID);
    const randomNumbers = Array.from({ length: 500 }, () => Math.floor(Math.random() * (200 - 1 + 1)) + 1);
    surveyRef.update({
        responses: firebase.firestore.FieldValue.arrayUnion({
            questionId: question.id,
            questionText: question.questionText,
            questionType: question.type,
            results: randomNumbers
        })
    });
}

export const writeRandomNumbersToRatingResults = async (surveyID: string, question: Question) => {
    const surveyRef = firestore.collection('surveys').doc(surveyID);
    const randomNumbers = Array.from({ length: 500 }, () => Math.floor(Math.random() * (5 - 1 + 1)) + 1);
    surveyRef.update({
        responses: firebase.firestore.FieldValue.arrayUnion({
            questionId: question.id,
            questionText: question.questionText,
            questionType: question.type,
            results: randomNumbers
        })
    });
}

const random_sentences = [
    "The wind howled through the trees, carrying whispers of forgotten stories with every gust.",
    "Beneath the quiet of the city streets, a hidden world buzzed with life, unnoticed by those in a hurry.",
    "She stared at the blank canvas for hours, unsure if the inspiration would ever return, but knowing that the first stroke would lead her somewhere unexpected.",
    "The coffee shop on the corner had become his favorite escape, not because of the coffee, but because it was a place where time seemed to slow down.",
    "There’s something magical about early mornings, when the world is still and the sun is just starting to peek over the horizon.",
    "He had traveled thousands of miles only to realize that what he was searching for had been within him all along.",
    "The old book was worn and tattered, its pages yellowed with age, but every story inside still held a spark of life.",
    "The distant mountains called to her, a reminder that there was always something new to discover beyond the familiar.",
    "In the silence of the library, she found comfort in the knowledge that every book was a doorway to another world.",
    "As the rain began to fall, the streets emptied, and the city seemed to breathe a sigh of relief.",
    "The garden was overgrown and wild, but to her, it was a place where everything felt alive with possibility.",
    "The sky turned a deep shade of purple as the sun set, casting long shadows across the deserted beach.",
    "He wrote letters that were never meant to be sent, finding solace in the words he would never speak aloud.",
    "The old clock on the wall ticked loudly, a constant reminder that time waits for no one.",
    "She had always been fascinated by the stars, wondering what stories were hidden in the spaces between them.",
    "With a single step, he crossed the threshold into a world that was both familiar and entirely new.",
    "The air was thick with the scent of pine and damp earth, grounding her in the moment as she hiked deeper into the forest.",
    "It was a simple life, but one filled with quiet moments of joy and contentment, far from the chaos of the outside world.",
    "He sat by the window, watching as the storm rolled in, the sky darkening like a curtain being drawn.",
    "In a world full of noise, she found peace in the silence, where her thoughts could finally settle.",
    "The smell of fresh rain lingered in the air, as if the earth had just taken a deep breath and exhaled in relief.",
    "She wandered through the antique store, feeling like every object had a story waiting to be told.",
    "The boat drifted aimlessly on the calm lake, the water still and glassy under the midday sun.",
    "There was something enchanting about the way the fog rolled over the hills, swallowing the landscape in a thick, white blanket.",
    "His old guitar sat in the corner, gathering dust, a reminder of the songs that once filled the room with life.",
    "In the heart of the city, amidst the noise and chaos, there was a small park where time seemed to stand still.",
    "The train's whistle echoed through the valley, a lonely sound in the quiet of the early morning.",
    "She had always loved thunderstorms, finding comfort in the rumble of the clouds and the sharp flashes of lightning.",
    "The carnival lights flickered in the distance, a beacon of color and excitement against the dark night sky.",
    "The sound of the ocean waves crashing against the shore was like music to his ears, a constant, soothing rhythm.",
    "The old bridge creaked underfoot, its wooden planks worn and weathered by years of use and neglect.",
    "They sat in comfortable silence, watching the fire crackle and pop, the warmth of the flames holding the cold at bay.",
    "The dusty road stretched out before them, disappearing into the horizon, a symbol of endless possibilities.",
    "She placed the last piece of the puzzle, finally completing the picture, and smiled at the sense of accomplishment.",
    "The city skyline glowed in the distance, the lights twinkling like stars against the darkened sky.",
    "He opened the letter with trembling hands, unsure if he was ready to face the words written on the page.",
    "The tiny café was filled with the rich aroma of coffee, the air buzzing with the chatter of early morning customers.",
    "She stood at the edge of the cliff, the wind whipping through her hair, feeling like she was on the verge of something monumental.",
    "The fireflies danced in the evening air, their soft glow illuminating the path through the forest.",
    "As the plane took off, she watched the world below shrink, the city becoming just a speck in the vast landscape.",
    "The distant thunder rumbled softly, a reminder that the storm was still far off, yet slowly approaching.",
    "She flipped through the pages of the old journal, the ink smudged in places, but the memories still vivid and alive.",
    "The cat sat perched on the windowsill, watching the world outside with a quiet curiosity, its tail swishing lazily.",
    "A single leaf floated down from the tree, carried gently by the breeze until it settled softly on the ground.",
    "The crowded market bustled with life, the smells of spices, fresh bread, and roasting coffee filling the air.",
    "He stared out at the vast ocean, feeling small and insignificant, yet strangely at peace.",
    "The children laughed as they ran through the sprinkler, their joy infectious and unrestrained.",
    "The old house creaked and groaned in the night, as if the walls themselves were alive and watching.",
    "A soft glow from the streetlight outside bathed the room in a gentle, golden light.",
    "The sound of a lone violin echoed through the empty hall, each note filled with emotion.",
    "She watched as the snow began to fall, tiny flakes swirling in the air like a scene from a dream.",
    "The mountain trail twisted and turned, revealing breathtaking views around every corner.",
    "The air was thick with the scent of blooming flowers, a fragrant reminder of the passing seasons.",
    "He couldn’t help but smile as he looked at the old photograph, a snapshot of a simpler time.",
    "The city skyline was a jagged silhouette against the orange and pink hues of the setting sun.",
    "The gentle hum of the refrigerator was the only sound in the otherwise quiet apartment.",
    "As she dipped her toes in the cool water, a sense of calm washed over her.",
    "The wind chimes tinkled softly in the breeze, their melody a delicate background to the evening.",
    "He flipped the coin in the air, watching as it spun, his future seemingly hanging in the balance.",
    "The bonfire crackled and popped, sending sparks dancing into the night sky.",
    "The early morning fog rolled in from the sea, blanketing the town in a soft, ethereal mist.",
    "She sipped her tea slowly, watching the raindrops race down the window in a quiet rhythm.",
    "The clock ticked loudly in the silence of the room, each second stretching longer than the last.",
    "He stood at the crossroads, unsure which path would lead him to where he needed to be.",
    "The smell of freshly baked bread filled the kitchen, inviting everyone to gather around the table.",
    "She always wondered what it would be like to just pack up and leave, no destination in mind, just the open road ahead.",
    "The stars twinkled brightly in the clear night sky, as if they were winking at the world below.",
    "He couldn’t shake the feeling that something was about to change, a shift in the air that felt almost tangible.",
    "The waves crashed against the shore, a constant reminder of nature’s power and beauty.",
    "She found comfort in the pages of her favorite book, each word a familiar friend in an unfamiliar world.",
    "The autumn leaves crunched beneath their feet as they walked down the path, the air crisp and cool.",
    "He sat by the fire, lost in thought, as the flames danced and flickered in front of him.",
    "The distant hum of the city was a backdrop to their conversation, a low murmur that faded into the background.",
    "She looked out over the canyon, the vastness of it taking her breath away as the sun set in brilliant hues of red and orange.",
    "The sound of laughter echoed through the house, a rare moment of pure joy that lingered long after the silence returned.",
    "The train ride was long, but the view of the mountains as they passed made every minute worth it.",
    "He held the old pocket watch in his hands, wondering about the stories it had witnessed over the years.",
    "The wind tugged at her coat as she walked along the pier, the scent of saltwater filling the air.",
    "They sat on the porch, watching the storm roll in, the first drops of rain hitting the roof with a soft patter.",
    "The narrow alley was lit by a single streetlamp, casting long shadows that seemed to stretch endlessly into the night."
]

export const writeRandomSentencesToShortResponseResults = async (surveyID: string, question: Question) => {
    const surveyRef = firestore.collection('surveys').doc(surveyID);
    const randomSentences = Array.from({ length: 500 }, () => random_sentences[Math.floor(Math.random() * random_sentences.length)]);
    surveyRef.update({
        responses: firebase.firestore.FieldValue.arrayUnion({
            questionId: question.id,
            questionText: question.questionText,
            questionType: question.type,
            results: randomSentences
        })
    });
}

